import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { AbstractControl, ControlContainer, FormControl, FormGroupDirective } from '@angular/forms';
import { map, debounceTime, filter, combineLatest, iif, of, startWith, switchMap, takeUntil, tap, Observable, BehaviorSubject, ReplaySubject, take, firstValueFrom } from 'rxjs';
import { DictionaryItem } from 'src/app/models/DictionaryItem';
import { DisableErrorStateMatcher } from 'src/app/shared/error-state-matchers/disable-error-state-matcher';
import { IPagedResult } from 'src/app/shared/models/PagedResult';
import { autocompleteValidator } from 'src/app/shared/validators/autocomplete.validator';
import { SubSink } from 'SubSink';
import { FilterDictionaryItem } from "../../../models/FilterDictionaryItem";

@Component({
  selector: 'app-searchable-multi-select-filter',
  templateUrl: './searchable-multi-select-filter.component.html',
  styleUrl: './searchable-multi-select-filter.component.scss',
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective
    }
  ],
  standalone: false
})
export class SearchableMultiSelectFilterComponent implements OnInit, OnDestroy {
  formGroupDirective = inject(FormGroupDirective);
  private changeDetectorRef = inject(ChangeDetectorRef);

  @Input() name: string;
  @Input() label?: string;
  @Input() placeholder?: string;
  @Input() debounceTime?: number = 500;
  @Input() pageSize?: number = 5;
  @Input() private timeBetweenInput?: number = 300;
  @Input() private minSearchTextLength?: number = 3;
  @Input() private showAllIfSearchIsEmpty?: boolean = false;
  @Input() getData: (value: string, page: number, pageSize: number) => Observable<IPagedResult<FilterDictionaryItem>>;
  @Input() changeCallback: (items: FilterDictionaryItem[]) => void;
  @Input() description?: string;

  public count$: ReplaySubject<number> = new ReplaySubject<number>(null);
  public page = 1;
  public searching = false;
  public notVisibleSelectedItems$: Observable<FilterDictionaryItem[]>;
  public isAllSelected$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public itemsFiltering: FormControl<string> = new FormControl<string>('');
  public filteredServerSideItems$: BehaviorSubject<FilterDictionaryItem[]> = new BehaviorSubject<FilterDictionaryItem[]>(null);
  public readonly matcher = new DisableErrorStateMatcher();
  public readonly inputControl = new FormControl(null, [autocompleteValidator]);
  private readonly subs = new SubSink();

  private get control(): AbstractControl {
    return this.formGroupDirective.form.get(this.name);
  }
  private get objControl(): AbstractControl {
    return this.formGroupDirective.form.get(`${this.name}Object`);
  }


  constructor() {
  }

  ngOnInit(): void {
    this.addControl();
    this.initEmployerObject();

    this.subs.sink = this.objControl.valueChanges.subscribe(value => {
      this.inputControl.setValue(value, { emitEvent: false });
      if (this.changeCallback) {
        this.changeCallback(value);
      }
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.removeControl();
  }

  initEmployerObject() {
    this.notVisibleSelectedItems$ = this.filteredServerSideItems$.pipe(map(filtered => this.inputControl?.value?.filter(selected => !filtered?.some(f => f.Id === selected.Id))));

    this.itemsFiltering.valueChanges
      .pipe(
        startWith(null),
        filter(search => this.showAllIfSearchIsEmpty || !search || search.length >= this.minSearchTextLength),
        tap(() => this.searching = true),
        debounceTime(this.timeBetweenInput),
        tap(() => this.page = 1),
        switchMap((value: string) => iif(() => (this.showAllIfSearchIsEmpty || !this.inputControl.value?.length) || !!value, this.getData(value, this.page, this.pageSize), of(null))),
      )
      .subscribe({
        next: filteredBanks => {
          this.searching = false;

          this.filteredServerSideItems$.next(filteredBanks?.Results);
          this.count$.next(filteredBanks?.Count);
        },
        error: () => this.searching = false
      });

    this.subs.sink = combineLatest([this.filteredServerSideItems$.pipe(startWith([])), this.inputControl.valueChanges.pipe(startWith(this.inputControl.value))])
      .pipe(
        map(([filteredServerSideBanks, employerObject]) => (filteredServerSideBanks?.length && filteredServerSideBanks.every(i => employerObject?.some(v => v.Id === i.Id))) || (!filteredServerSideBanks?.length && !!employerObject?.length && this.itemsFiltering.value?.length < this.minSearchTextLength)),)
      .subscribe(val => this.isAllSelected$.next(val));

    this.subs.sink = this.inputControl.valueChanges
      .pipe(debounceTime(this.timeBetweenInput))
      .subscribe((values: any) => {
        if (values?.length) {
          this.objControl?.setValue(values, { emitEvent: false });
          this.control.setValue(values.map(value => value.Id), { emitEvent: true });

          if (this.changeCallback) {
            this.changeCallback(values);
          }
        } else {
          this.objControl?.setValue(null, { emitEvent: false });
          this.control.setValue([], { emitEvent: true });

          if (this.changeCallback) {
            this.changeCallback([]);
          }
        }
      });
  }

  toggleSelectAll(selectAllValue: boolean) {
    this.isAllSelected$.next(selectAllValue);

    if (selectAllValue) {
      combineLatest([this.filteredServerSideItems$, this.notVisibleSelectedItems$])
        .pipe(
          map(([a, b]) => [...b ?? [], ...a ?? []]),
          take(1),
          takeUntil(this.itemsFiltering.valueChanges))
        .subscribe(val => {
          this.changeDetectorRef.detectChanges();
          this.inputControl.patchValue(val);

        });
    } else {
      this.inputControl.patchValue([]);
    }
  }

  loadMore() {
    const name = this.itemsFiltering.value;

    if (this.showAllIfSearchIsEmpty || name) {
      this.getData(name, ++this.page, this.pageSize)
        .pipe(
          tap(() => this.searching = true)
        )
        .subscribe({
          next: async filteredBanks => {
            this.searching = false;

            const filteredServerSideBanks = await firstValueFrom(this.filteredServerSideItems$);

            this.filteredServerSideItems$.next([...filteredServerSideBanks, ...filteredBanks.Results]);
            this.count$.next(filteredBanks.Count);
          },
          error: () => this.searching = false
        });
    }
  }

  public displayValueFn(value: DictionaryItem): string | undefined {
    return value?.Name;
  }

  compareFunc(a: DictionaryItem, b: DictionaryItem) {
    return a && b && a.Id === b.Id;
  }

  private addControl(): void {
    this.formGroupDirective.form.addControl(this.name, new FormControl(null), { emitEvent: false });
    this.formGroupDirective.form.addControl(`${this.name}Object`, new FormControl(null), { emitEvent: false });
  }

  private removeControl(): void {
    this.formGroupDirective.form.removeControl(this.name, { emitEvent: false });
    this.formGroupDirective.form.removeControl(`${this.name}Object`, { emitEvent: false });
  }
}
